/**
 * CPT Section Listing — tab filter interaction + entrance choreography.
 *
 * Drives leadership / board / financials / honoree / location listings:
 *  - Pill-style tab bar toggles the visible panel.
 *  - Initial visible panel gets a staggered fade-up across its cards on
 *    scroll-into-view. Only the FIRST 9 cards animate (3-col × 3 rows worth);
 *    larger lists snap in to keep entrance time bounded.
 *  - When the user switches tabs, the newly-revealed panel staggers again
 *    so the action feels responsive instead of an instant swap. This re-fire
 *    is intentional — it's tied to user input, not scroll position.
 *
 * No-JS visitors see the first panel and full content (animations are
 * progressive enhancement only).
 */

import { revealStagger, motionTokens } from '@/motion-primitives';

const ACTIVE_CLASSES = ['btn-solid-red', 'hover:bg-rmh-red'];
const INACTIVE_CLASSES = [
	'bg-rmh-soft-white',
	'border-rmh-black/10',
	'hover:bg-rmh-red',
	'hover:text-rmh-soft-white',
];

// Tighter stagger than the global default — long lists like the leadership
// directors panel (30+ cards) would feel sluggish at the standard pace.
// Keep all cards animated; capping at 9 leaves the rest visible from the start
// and reads as broken.
const STAGGER = 0.04;

const BASE_REVEAL_OPTS = {
	childSelector: ':scope > *',
	stagger: STAGGER,
	duration: motionTokens.durMd,
	// Trigger as soon as any part of the grid enters the viewport. The default
	// 0.15 fires only after 15% of the grid is visible, which on long listings
	// (e.g. /our-story/board/ with 30+ cards) means the user has to scroll
	// well past the section's top before the cards start populating.
	amount: 'some',
};

function revealPanel(panel, { immediate = false } = {}) {
	const grid = panel.querySelector('.grid, .flex.flex-col');
	if (grid) {
		revealStagger(grid, { ...BASE_REVEAL_OPTS, immediate });
	}
}

function initListing(root) {
	const tabs = root.querySelectorAll('[data-tab-target]');
	const panels = root.querySelectorAll('[data-panel]');

	// Initial paint: reveal whichever panel is visible (no .hidden class).
	const initialPanel = root.querySelector('.rmh-cpt-section-panel:not(.hidden)');
	if (initialPanel) {
		revealPanel(initialPanel);
	}

	if (!tabs.length || !panels.length) return;

	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			const slug = tab.getAttribute('data-tab-target');
			tabs.forEach((t) => {
				const isActive = t === tab;
				t.setAttribute('aria-selected', isActive ? 'true' : 'false');
				ACTIVE_CLASSES.forEach((c) => t.classList.toggle(c, isActive));
				INACTIVE_CLASSES.forEach((c) => t.classList.toggle(c, !isActive));
			});

			let revealed = null;
			panels.forEach((p) => {
				const isTarget = p.getAttribute('data-panel') === slug;
				p.classList.toggle('hidden', !isTarget);
				if (isTarget) revealed = p;
			});

			if (revealed) {
				// Reveal the new panel's cards immediately — the user just
				// clicked the tab, so we don't want to wait for inView. On
				// mobile especially, the new panel's top cards may already be
				// at the user's scroll position, but a tall panel below them
				// would otherwise need scrolling before any animation fires.
				revealPanel(revealed, { immediate: true });
			}
		});
	});
}

function mount() {
	document.querySelectorAll('[data-cpt-section-listing]').forEach(initListing);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mount);
} else {
	mount();
}
